import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, MoveUp, MoveDown, Eye, Save, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AVAILABLE_ROOM_TYPES } from '@/utils/flow-data';
import { AssetsConfig } from '@/services/AssetsConfig';
import { ProjectService } from '@/services/ProjectService';

const questionTypes = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email Input' },
  { value: 'tel', label: 'Phone Number' },
  { value: 'number', label: 'Number Input' },
  { value: 'date', label: 'Date Input' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'multiple_select', label: 'Multiple Select' },
  // { value: 'file_upload', label: 'File Upload' },
  { value: 'zipcode', label: 'ZIP Code' },
  { value: 'address', label: 'Full Address' }
];

const spacialQuestionTypes = [
    {
      value: 'zipcode',
      label: 'ZIP Code',
      validation: {
        pattern: '^[0-9]{5}(?:-[0-9]{4})?$',
        message: 'Please enter a valid US ZIP code'
      }
    },
    {
      value: 'address',
      label: 'Full Address',
      structure: [
        { value: 'street1', label: 'Street Address', type: 'text', required: true },
        { value: 'street2', label: 'Apartment, suite, etc.', type: 'text', required: false },
        { value: 'city', label: 'City', type: 'text', required: true },
        { value: 'state', label: 'State', type: 'select', required: true },
        { value: 'zipCode', label: 'ZIP Code', type: 'zipcode', required: true }
      ]
    }
  ];


const DEFAULT_CONFIG = {
  sections: [
    {
      id: "contactDetails",
      title: "Contact Details",
      type: "group",
      icon: "😎",
      fields: [
        {
          id: "projectFor",
          type: "radio",
          label: "Who is this project for?",
          required: true,
          options: ["For me", "For someone else"]
        },
        {
          id: "customerName",
          type: "text",
          label: "Name",
          placeholder: "Name",
          required: true,
          conditions: [
            {
              questionId: "projectFor",
              operator: "equals",
              value: "For someone else"
            }
          ]
        },
        {
          id: "email",
          type: "email",
          label: "Email",
          placeholder: "Email",
          required: true,
          conditions: [
            {
              questionId: "projectFor",
              operator: "equals",
              value: "For someone else"
            }
          ]
        }
      ]
    },
    {
      id: "collectionDetails",
      title: "Project Details",
      icon: "💼",
      type: "group",
      fields: [
        {
          id: "collectionName",
          type: "text",
          label: "Project Name",
          placeholder: "Enter a name for your collection",
          required: true,
          defaultValue: "Room Renovation"
        }
      ]
    },
    {
      id: "rooms",
      icon: "🏘️",
      title: "What rooms are you planning to renovate?",
      type: "checkboxGroup",
      required: true,
      options: [
        {
          id: "kitchen",
          label: "Kitchen",
          allowMultiple: true,
          isVisible: true
        }
      ]
    }
  ]
};

// Helper function to merge configurations
const mergeConfigs = (existingConfig, defaultConfig) => {
  if (!existingConfig || !existingConfig.sections) {
    return defaultConfig;
  }

  const mergedSections = defaultConfig.sections.map(defaultSection => {
    const existingSection = existingConfig.sections.find(s => s.id === defaultSection.id);
    if (!existingSection) {
      return defaultSection;
    }

    // For rooms section, ensure default rooms are present
    if (defaultSection.id === 'rooms') {
      const mergedOptions = [...defaultSection.options];
      existingSection.options?.forEach(option => {
        if (!mergedOptions.find(o => o.id === option.id)) {
          mergedOptions.push(option);
        }
      });
      return {
        ...existingSection,
        options: mergedOptions
      };
    }

    // For other sections, ensure default fields are present
    const mergedFields = defaultSection.fields.map(defaultField => {
      const existingField = existingSection.fields?.find(f => f.id === defaultField.id);
      return existingField ? { ...defaultField, ...existingField } : defaultField;
    });

    // Add any additional fields from existing config
    existingSection.fields?.forEach(field => {
      if (!mergedFields.find(f => f.id === field.id)) {
        mergedFields.push(field);
      }
    });

    return {
      ...existingSection,
      fields: mergedFields
    };
  });

  // Add any additional sections from existing config
  existingConfig.sections.forEach(section => {
    if (!mergedSections.find(s => s.id === section.id)) {
      mergedSections.push(section);
    }
  });

  return {
    ...existingConfig,
    sections: mergedSections
  };
};

const ConfigEditor = () => {
  const [config, setConfig] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [currentSection, setCurrentSection] = useState('projectFor');
  const [showJson, setShowJson] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      try{AssetsConfig.clearCache();}
      catch(error){console.error("Can't clear cache", error)}
      const configData = await AssetsConfig.getConfigJSON();
      const mergedConfig = mergeConfigs(configData, DEFAULT_CONFIG);
      setConfig(mergedConfig);
    } catch (error) {
      console.error("Error loading configuration:", error);
      setConfig(DEFAULT_CONFIG);
    }
  };

  const handleSave = async () => {
    try {
      await ProjectService.uploadStartProjectConfig(config);
      toast({
        title: "Success",
        description: "Configuration saved successfully!",
      });
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast({
        title: "Error",
        description: "Failed to save configuration.",
        variant: "destructive",
      });
    }
  };

  const updateSection = (sectionId, updates) => {
    setConfig(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, ...updates }
          : section
      )
    }));
  };

  const addField = (sectionId) => {
    const newField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      placeholder: 'Enter value',
      required: false,
      visible: true,
      helpText: '',
      icon: '❓',
      conditions: []
    };

    updateSection(sectionId, {
      fields: [...config.sections.find(s => s.id === sectionId).fields, newField]
    });
  };

  const moveField = (sectionId, fieldId, direction) => {
    const section = config.sections.find(s => s.id === sectionId);
    const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
    const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;

    if (newIndex < 0 || newIndex >= section.fields.length) return;

    const newFields = [...section.fields];
    [newFields[fieldIndex], newFields[newIndex]] = [newFields[newIndex], newFields[fieldIndex]];

    updateSection(sectionId, { fields: newFields });
  };

  const renderField = (section, field) => {
    const isDefaultField = DEFAULT_CONFIG.sections
      .find(s => s.id === section.id)
      ?.fields?.some(f => f.id === field.id);

    return (
      <Card key={field.id} className="mb-4">
        <CardContent className="pt-6">
          <div className="flex gap-2 mb-4">
            {!isDefaultField && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveField(section.id, field.id, 'up')}
                >
                  <MoveUp className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveField(section.id, field.id, 'down')}
                >
                  <MoveDown className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    const updatedFields = section.fields.filter(f => f.id !== field.id);
                    updateSection(section.id, { fields: updatedFields });
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label>Question Label</Label>
              <Input
                value={field.label}
                onChange={(e) => {
                  const updatedFields = section.fields.map(f =>
                    f.id === field.id ? { ...f, label: e.target.value } : f
                  );
                  updateSection(section.id, { fields: updatedFields });
                }}
                placeholder="Enter field label"
              />
            </div>
            <div className="w-24">
              <Label>Icon</Label>
              <Input
                value={field.icon || ''}
                onChange={(e) => {
                  const updatedFields = section.fields.map(f =>
                    f.id === field.id ? { ...f, icon: e.target.value } : f
                  );
                  updateSection(section.id, { fields: updatedFields });
                }}
                placeholder="Icon"
              />
            </div>
          </div>

            {!['customerName', 'email'].includes(field.id) && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Question Type</Label>
                  <Select
                    value={field.type}
                    onValueChange={(value) => {
                      // If it's a default field (projectFor), don't allow type change
                      if (field.id === 'projectFor') return;
                      
                      // const updatedFields = section.fields.map(f =>
                      //   f.id === field.id ? {
                      //     ...f,
                      //     type: value,
                      //     options: ['select', 'radio', 'multiple_select'].includes(value) ? [] : undefined
                      //   } : f
                      // );
                      const updatedFields = section.fields.map(f => {
                        if (f.id === field.id) {
                          // Start with basic field update
                          let updatedField = {
                            ...f,
                            type: value,
                            // Clear options if switching from select/radio/multiple_select
                            options: ['select', 'radio', 'multiple_select'].includes(value) ? [] : undefined
                          };
                      
                          // Check if this is a special question type
                          const specialType = spacialQuestionTypes.find(type => type.value === value);
                          if (specialType) {
                            // Add validation or structure from the special type definition
                            if (specialType.validation) {
                              updatedField.validation = specialType.validation;
                            }
                            if (specialType.structure) {
                              updatedField.structure = specialType.structure;
                            }
                          }
                      
                          return updatedField;
                        }
                        return f;
                      });
                      updateSection(section.id, { fields: updatedFields });
                    }}
                    disabled={field.id === 'projectFor'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {questionTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              <div className="space-y-2">
                <Label>Required</Label>
                <div>
                  <Switch
                    checked={field.required}
                    onCheckedChange={(checked) => {
                      const updatedFields = section.fields.map(f =>
                        f.id === field.id ? { ...f, required: checked } : f
                      );
                      updateSection(section.id, { fields: updatedFields });
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Visible</Label>
                <div>
                  <Switch
                    checked={field.visible !== false}
                    onCheckedChange={(checked) => {
                      const updatedFields = section.fields.map(f =>
                        f.id === field.id ? { ...f, visible: checked } : f
                      );
                      updateSection(section.id, { fields: updatedFields });
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Help Text</Label>
            <Input
              value={field.helpText || ''}
              onChange={(e) => {
                const updatedFields = section.fields.map(f =>
                  f.id === field.id ? { ...f, helpText: e.target.value } : f
                );
                updateSection(section.id, { fields: updatedFields });
              }}
              placeholder="Enter help text"
            />
          </div>

          {/* Special Type Configuration */}
          {spacialQuestionTypes.map(specialType => {
            if (field.type === specialType.value) {
              return (
                <div key={specialType.value} className="mt-4">
                  {/* Validation Fields */}
                  {specialType.validation && (
                    <>
                      <Label>{specialType.label} Validation</Label>
                      {Object.entries(specialType.validation).map(([key, value]) => (
                        <Input
                          key={key}
                          className="mt-2"
                          value={field.validation?.[key] || ''}
                          onChange={(e) => {
                            const updatedFields = section.fields.map(f =>
                              f.id === field.id ? {
                                ...f,
                                validation: {
                                  ...f.validation,
                                  [key]: e.target.value
                                }
                              } : f
                            );
                            updateSection(section.id, { fields: updatedFields });
                          }}
                          placeholder={`${key.charAt(0).toUpperCase() + key.slice(1)}`}
                        />
                      ))}
                    </>
                  )}

                  {/* Structure Fields */}
                  {specialType.structure && (
                    <>
                      <Label>{specialType.label} Components</Label>
                      <div className="space-y-2 mt-2">
                        {field.structure?.map((component, index) => (
                          <Card key={index} className="p-4">
                            <div className="grid gap-2">
                              <Input
                                value={component.label}
                                onChange={(e) => {
                                  const updatedStructure = [...field.structure];
                                  updatedStructure[index] = {
                                    ...component,
                                    label: e.target.value
                                  };
                                  const updatedFields = section.fields.map(f =>
                                    f.id === field.id ? {
                                      ...f,
                                      structure: updatedStructure
                                    } : f
                                  );
                                  updateSection(section.id, { fields: updatedFields });
                                }}
                                placeholder="Component label"
                              />
                              <div className="flex items-center gap-2">
                                <Label>Required</Label>
                                <Switch
                                  checked={component.required}
                                  onCheckedChange={(checked) => {
                                    const updatedStructure = [...field.structure];
                                    updatedStructure[index] = {
                                      ...component,
                                      required: checked
                                    };
                                    const updatedFields = section.fields.map(f =>
                                      f.id === field.id ? {
                                        ...f,
                                        structure: updatedStructure
                                      } : f
                                    );
                                    updateSection(section.id, { fields: updatedFields });
                                  }}
                                />
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            }
            return null;
          })}

          <div className="mt-4">
            <Label>Conditions</Label>
            {!field.conditions && !['customerName', 'email'].includes(field.id) ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const updatedFields = section.fields.map(f =>
                    f.id === field.id ? {
                      ...f,
                      conditions: [{
                        questionId: '',
                        operator: 'equals',
                        value: ''
                      }]
                    } : f
                  );
                  updateSection(section.id, { fields: updatedFields });
                }}
                className="mt-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Condition
              </Button>
            ) : (
              <div className="space-y-4 mt-2">
                {field.conditions?.map((condition, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label>Depends on Question</Label>
                          <Select
                            value={condition.questionId}
                            onValueChange={(value) => {
                              // Prevent changes for customerName and email fields
                              if (['customerName', 'email'].includes(field.id)) return;
                              
                              const updatedFields = section.fields.map(f => {
                                if (f.id === field.id) {
                                  const updatedConditions = [...f.conditions];
                                  updatedConditions[index] = {
                                    ...condition,
                                    questionId: value,
                                    value: ''
                                  };
                                  return { ...f, conditions: updatedConditions };
                                }
                                return f;
                              });
                              updateSection(section.id, { fields: updatedFields });
                            }}
                            disabled={['customerName', 'email'].includes(field.id)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select question" />
                            </SelectTrigger>
                            <SelectContent>
                              {section.fields
                                .filter(f => 
                                  f.id !== field.id && 
                                  section.fields.indexOf(f) < section.fields.indexOf(field)
                                )
                                .map(f => (
                                  <SelectItem key={f.id} value={f.id}>
                                    {f.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-32">
                          <Label>Operator</Label>
                          <Select
                            value={condition.operator}
                            onValueChange={(value) => {
                              // Prevent changes for customerName and email fields
                              if (['customerName', 'email'].includes(field.id)) return;
                              
                              const updatedFields = section.fields.map(f => {
                                if (f.id === field.id) {
                                  const updatedConditions = [...f.conditions];
                                  updatedConditions[index] = {
                                    ...condition,
                                    operator: value
                                  };
                                  return { ...f, conditions: updatedConditions };
                                }
                                return f;
                              });
                              updateSection(section.id, { fields: updatedFields });
                            }}
                            disabled={['customerName', 'email'].includes(field.id)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                              <SelectItem value="not_contains">Not Contains</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Value</Label>
                        <Input
                          value={condition.value}
                          onChange={(e) => {
                            // Prevent changes for customerName and email fields
                            if (['customerName', 'email'].includes(field.id)) return;
                            
                            const updatedFields = section.fields.map(f => {
                              if (f.id === field.id) {
                                const updatedConditions = [...f.conditions];
                                updatedConditions[index] = {
                                  ...condition,
                                  value: e.target.value
                                };
                                return { ...f, conditions: updatedConditions };
                              }
                              return f;
                            });
                            updateSection(section.id, { fields: updatedFields });
                          }}
                          disabled={['customerName', 'email'].includes(field.id)}
                          placeholder="Enter value"
                        />
                      </div>

                      {!['customerName', 'email'].includes(field.id) && (
                        <div className="flex justify-between">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              const updatedFields = section.fields.map(f => {
                                if (f.id === field.id) {
                                  const updatedConditions = f.conditions.filter((_, i) => i !== index);
                                  return {
                                    ...f,
                                    conditions: updatedConditions.length ? updatedConditions : undefined
                                  };
                                }
                                return f;
                              });
                              updateSection(section.id, { fields: updatedFields });
                            }}
                          >
                            Remove Condition
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
                
                {!['customerName', 'email'].includes(field.id) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const updatedFields = section.fields.map(f => {
                        if (f.id === field.id) {
                          return {
                            ...f,
                            conditions: [
                              ...f.conditions,
                              {
                                questionId: '',
                                operator: 'equals',
                                value: ''
                              }
                            ]
                          };
                        }
                        return f;
                      });
                      updateSection(section.id, { fields: updatedFields });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Condition
                  </Button>
                )}
              </div>
            )}
          </div>

          {['select', 'radio', 'multiple_select'].includes(field.type) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                {field.id !== 'projectFor' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const updatedFields = section.fields.map(f => {
                        if (f.id === field.id) {
                          return {
                            ...f,
                            options: [...(f.options || []), `Option ${(f.options || []).length + 1}`]
                          };
                        }
                        return f;
                      });
                      updateSection(section.id, { fields: updatedFields });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {field.options?.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={option}
                      onChange={(e) => {
                        if (field.id === 'projectFor') return; // Prevent editing for projectFor options
                        const updatedFields = section.fields.map(f => {
                          if (f.id === field.id) {
                            const newOptions = [...f.options];
                            newOptions[index] = e.target.value;
                            return { ...f, options: newOptions };
                          }
                          return f;
                        });
                        updateSection(section.id, { fields: updatedFields });
                      }}
                      placeholder="Option text"
                      disabled={field.id === 'projectFor'}
                    />
                    {field.id !== 'projectFor' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          const updatedFields = section.fields.map(f => {
                            if (f.id === field.id) {
                              return {
                                ...f,
                                options: f.options.filter((_, i) => i !== index)
                              };
                            }
                            return f;
                          });
                          updateSection(section.id, { fields: updatedFields });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

  const renderSectionContent = (section) => {
    if (!section) return null;

    if (section.id === 'rooms') {
      return (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div>
                <Label>Section Title</Label>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(section.id, { title: e.target.value })}
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <Label>Section Icon</Label>
                <Input
                  value={section.icon || ''}
                  onChange={(e) => updateSection(section.id, { icon: e.target.value })}
                  placeholder="Enter section icon (emoji)"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Available Rooms</h3>
            <Select
              onValueChange={(roomType) => {
                const roomToAdd = AVAILABLE_ROOM_TYPES.find(r => r.id === roomType);
                if (roomToAdd && !section.options.some(r => r.id === roomToAdd.id)) {
                  updateSection(section.id, {
                    options: [...section.options, { 
                      ...roomToAdd, 
                      allowMultiple: true,
                      isVisible: true 
                    }]
                  });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Add room type..." />
              </SelectTrigger>
              <SelectContent>
                {AVAILABLE_ROOM_TYPES
                  .filter(room => !section.options.some(r => r.id === room.id))
                  .map(room => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4">
            {section.options.map(room => (
              <Card key={room.id} className="p-4">
                <div className="flex items-center justify-between">
                  <span>{room.label}</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`${room.id}-visible`}>Visible</Label>
                      <Switch
                        id={`${room.id}-visible`}
                        checked={room.isVisible}
                        onCheckedChange={(checked) => {
                          const updatedOptions = section.options.map(r =>
                            r.id === room.id ? { ...r, isVisible: checked } : r
                          );
                          updateSection(section.id, { options: updatedOptions });
                        }}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const updatedOptions = section.options.filter(r => r.id !== room.id);
                        updateSection(section.id, { options: updatedOptions });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4">
              <div>
                <Label>Section Title</Label>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(section.id, { title: e.target.value })}
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <Label>Section Icon</Label>
                <Input
                  value={section.icon || ''}
                  onChange={(e) => updateSection(section.id, { icon: e.target.value })}
                  placeholder="Enter section icon (emoji)"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Questions</h3>
          <Button onClick={() => addField(section.id)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </div>

        <div className="space-y-4">
          {section.fields?.map(field => renderField(section, field))}
        </div>
      </div>
    );
  };

  if (!config) return null;

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Start Project Configuration</h1>
        <div className="space-x-2">
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={() => setShowJson(!showJson)}>
            {showJson ? 'Hide' : 'Show'} JSON
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-2">
          {config.sections.map(section => (
            <div
              key={section.id}
              className={`p-2 rounded cursor-pointer ${
                currentSection === section.id ? 'bg-blue-100' : 'bg-gray-100'
              }`}
              onClick={() => setCurrentSection(section.id)}
            >
              {/* <span>{section.icon} {section.title}</span> */}
              <span>{section.icon} {
                section.id === 'contactDetails' ? 'Contact Details' :
                section.id === 'collectionDetails' ? 'Project Info' :
                section.id === 'rooms' ? 'Rooms' :
                section.title
              }</span>
            </div>
          ))}
        </div>

        <div className="col-span-3">
          {renderSectionContent(config.sections.find(s => s.id === currentSection))}
        </div>
      </div>

      {showJson && (
        <Card className="mt-6">
          <CardHeader>
            <h3 className="font-bold">Generated JSON</h3>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
              {JSON.stringify(config, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConfigEditor;





